import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiResponse, IdentityBoard, EraDecade, SchoolType, CollegeType, CoreBlocksData } from '@/api/types';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';

interface CoreBlockProps {
  onComplete: () => void;
}

// The birth years are only a hint: the decade is the one a member grew up in.
const ERA_DECADES: { value: EraDecade; label: string; years: string }[] = [
  { value: 'era_50s', label: '50s Era', years: 'Born ~1945-1954' },
  { value: 'era_60s', label: '60s Era', years: 'Born ~1955-1964' },
  { value: 'era_70s', label: '70s Era', years: 'Born ~1965-1974' },
  { value: 'era_80s', label: '80s Era', years: 'Born ~1975-1984' },
  { value: 'era_90s', label: '90s Era', years: 'Born ~1985-1994' },
  { value: 'era_2000s', label: '2000s Era', years: 'Born ~1995-2004' },
  { value: 'era_2010s', label: '2010s Era', years: 'Born ~2005-2014' },
  { value: 'era_2020s', label: '2020s Era', years: 'Grew up in the 2020s' },
];

const SCHOOL_TYPES: { value: SchoolType; label: string }[] = [
  { value: 'convent', label: 'Convent/Missionary School' },
  { value: 'kv', label: 'Kendriya Vidyalaya' },
  { value: 'dps_dav', label: 'DPS/DAV/Public School' },
  { value: 'cbse_private', label: 'CBSE Private School' },
  { value: 'icse', label: 'ICSE School' },
  { value: 'state_board', label: 'State Board School' },
  { value: 'boarding', label: 'Boarding School' },
  { value: 'international', label: 'International School' },
  { value: 'government', label: 'Government School' },
  { value: 'home_school', label: 'Home Schooled' },
  { value: 'other_school', label: 'Other' },
];

const COLLEGE_TYPES: { value: CollegeType; label: string }[] = [
  { value: 'iit', label: 'IIT' },
  { value: 'nit', label: 'NIT' },
  { value: 'bits', label: 'BITS Pilani' },
  { value: 'iim', label: 'IIM' },
  { value: 'central_univ', label: 'Central University (DU, JNU, BHU)' },
  { value: 'state_engg', label: 'State Engineering College' },
  { value: 'state_univ', label: 'State University' },
  { value: 'private_engg', label: 'Private Engineering College' },
  { value: 'private_univ', label: 'Private University' },
  { value: 'medical', label: 'Medical College' },
  { value: 'regional', label: 'Regional College' },
  { value: 'abroad', label: 'Studied Abroad' },
  { value: 'no_college', label: 'Did not attend' },
  { value: 'other_college', label: 'Other' },
];

export default function CoreBlock({ onComplete }: CoreBlockProps) {
  const queryClient = useQueryClient();

  // Fetch existing board data
  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['identity-board'],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>('/identity-board'),
  });

  const board = boardData?.data;
  const coreBlocks = board?.core;

  // Form state
  const [eraDecade, setEraDecade] = useState<EraDecade | ''>('');
  const [eraLabel, setEraLabel] = useState('');
  const [schoolType, setSchoolType] = useState<SchoolType | ''>('');
  const [collegeType, setCollegeType] = useState<CollegeType | ''>('');
  const [communityMatchOptin, setCommunityMatchOptin] = useState(true);
  const [religionMatchOptin, setReligionMatchOptin] = useState(false);

  // Initialize from board data
  useEffect(() => {
    if (coreBlocks) {
      setEraDecade(coreBlocks.eraDecade || '');
      setEraLabel(coreBlocks.eraLabel || '');
      setSchoolType(coreBlocks.schoolType || '');
      setCollegeType(coreBlocks.collegeType || '');
      setCommunityMatchOptin(coreBlocks.communityMatchOptin ?? true);
      setReligionMatchOptin(coreBlocks.religionMatchOptin ?? false);
    }
  }, [coreBlocks]);

  // Update mutation - use block-specific endpoint
  const updateCoreBlocks = useMutation({
    mutationFn: async (data: CoreBlocksData) => {
      return api.patch<ApiResponse<IdentityBoard>>('/identity-board/blocks/core', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['identity-board'] });
      queryClient.invalidateQueries({ queryKey: ['identity-board-completeness'] });
      onComplete();
    },
  });

  const handleSubmit = () => {
    if (!eraDecade) return;

    updateCoreBlocks.mutate({
      eraDecade,
      eraLabel: eraLabel || undefined,
      schoolType: schoolType || undefined,
      collegeType: collegeType || undefined,
      communityMatchOptin,
      religionMatchOptin,
    });
  };

  if (boardLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Era Decade */}
      <div>
        <label className="label">What era shaped you? *</label>
        <p className="text-sm text-gray-500 mb-3">
          This helps us match you with people from your generation
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ERA_DECADES.map((era) => (
            <button
              key={era.value}
              type="button"
              onClick={() => setEraDecade(era.value)}
              className={clsx(
                'p-3 rounded-lg border-2 text-left transition-all',
                eraDecade === era.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <span className="font-medium block">{era.label}</span>
              <span className="text-xs text-gray-500">{era.years}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Era Label */}
      <div>
        <label htmlFor="eraLabel" className="label">
          Era Label (optional)
        </label>
        <input
          type="text"
          id="eraLabel"
          value={eraLabel}
          onChange={(e) => setEraLabel(e.target.value)}
          placeholder="e.g., 90s Bollywood kid, Doordarshan generation"
          className="input"
          maxLength={30}
        />
        <p className="text-xs text-gray-500 mt-1">
          Add a fun label that describes your era experience
        </p>
      </div>

      {/* School Type */}
      <div>
        <label className="label">School Type (optional)</label>
        <select
          value={schoolType}
          onChange={(e) => setSchoolType(e.target.value as SchoolType)}
          className="input"
        >
          <option value="">Select your school type</option>
          {SCHOOL_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* College Type */}
      <div>
        <label className="label">College Type (optional)</label>
        <select
          value={collegeType}
          onChange={(e) => setCollegeType(e.target.value as CollegeType)}
          className="input"
        >
          <option value="">Select your college type</option>
          {COLLEGE_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Match Preferences */}
      <div className="border-t pt-6">
        <label className="label mb-3">Match Preferences</label>
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={communityMatchOptin}
              onChange={(e) => setCommunityMatchOptin(e.target.checked)}
              className="checkbox mt-0.5"
            />
            <div>
              <span className="font-medium">Match on Community</span>
              <p className="text-sm text-gray-500">
                Include community background in matching
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={religionMatchOptin}
              onChange={(e) => setReligionMatchOptin(e.target.checked)}
              className="checkbox mt-0.5"
            />
            <div>
              <span className="font-medium">Match on Religion</span>
              <p className="text-sm text-gray-500">
                Include religious background in matching
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!eraDecade || updateCoreBlocks.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {updateCoreBlocks.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Save & Continue'
          )}
        </button>
        {updateCoreBlocks.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
